import React, { useState, useRef, useCallback } from 'react';
import { UploadCloud, X, Image as ImageIcon, CheckCircle2, AlertCircle } from 'lucide-react';

const CustomImageUploader = ({ isOpen, onClose, onSelectImage }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const processFile = (file) => {
        setError(null);
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setError('Please upload a valid image file (JPEG, PNG, GIF, WebP).');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setError('Image must be less than 5MB.');
            return;
        }

        // Create a local blob URL for instant UI feedback
        const blobUrl = URL.createObjectURL(file);
        onSelectImage(blobUrl);
        onClose();
    };

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files?.length) {
            processFile(files[0]);
        }
    }, [onClose, onSelectImage]);

    const handleFileSelect = (e) => {
        const files = e.target.files;
        if (files?.length) {
            processFile(files[0]);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(15, 23, 42, 0.6)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                animation: 'fadeIn 0.2s ease-out'
            }}
            onClick={onClose}
        >
            <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .uploader-dropzone { border: 2px dashed #e2e8f0; background: #f8fafc; transition: all 0.2s ease; }
        .uploader-dropzone.dragging { border-color: #6366f1; background: #eef2ff; transform: scale(1.02); }
        .uploader-dropzone:hover { border-color: #818cf8; background: #f1f5f9; cursor: pointer; }
      `}</style>

            <div
                style={{
                    width: '100%',
                    maxWidth: '560px',
                    backgroundColor: '#ffffff',
                    borderRadius: '24px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                    position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{
                    padding: '24px 32px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #f1f5f9'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ImageIcon size={20} color="#6366f1" />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#0f172a', letterSpacing: '-0.3px' }}>
                                Upload Image
                            </h2>
                            <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#64748b' }}>
                                Add visuals to your email design
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            padding: '8px',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            color: '#94a3b8',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.2s, color 0.2s'
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; }}
                        onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: '32px' }}>

                    {error && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', background: '#fef2f2',
                            color: '#ef4444', borderRadius: '12px', marginBottom: '24px', fontSize: '14px', fontWeight: '500'
                        }}>
                            <AlertCircle size={16} />
                            {error}
                        </div>
                    )}

                    <div
                        className={`uploader-dropzone ${isDragging ? 'dragging' : ''}`}
                        style={{
                            padding: '48px 24px',
                            borderRadius: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center'
                        }}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <div style={{
                            width: '64px', height: '64px', borderRadius: '50%', background: isDragging ? '#e0e7ff' : '#ffffff',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px',
                            transition: 'all 0.2s ease', transform: isDragging ? 'scale(1.1)' : 'scale(1)'
                        }}>
                            <UploadCloud size={28} color={isDragging ? '#4f46e5' : '#6366f1'} />
                        </div>
                        <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                            Drag & drop your image here
                        </h3>
                        <p style={{ margin: 0, fontSize: '14px', color: '#64748b', maxWidth: '280px', lineHeight: 1.5 }}>
                            Or click to browse your files. Supports JPEG, PNG, GIF, and WebP up to 5MB.
                        </p>
                    </div>

                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        style={{ display: 'none' }}
                    />

                    <div style={{ marginTop: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '13px', fontWeight: '500' }}>
                            <CheckCircle2 size={16} color="#10b981" /> Drag & Drop Support
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '13px', fontWeight: '500' }}>
                            <CheckCircle2 size={16} color="#10b981" /> Instant Insertion
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default CustomImageUploader;
